import { create } from 'zustand';
import { personMergesApi } from '../lib/api';
import { apiErrorMessage } from '../lib/apiError';
import type { PersonMergeDecideAction, PersonMergeItem, PersonMergeStatus } from '../types';

interface PersonMergeState {
  merges: PersonMergeItem[];
  queueCount: number;
  isLoading: boolean;
  isSaving: boolean;
  error: string | null;
  fetch: (params?: { status?: PersonMergeStatus }) => Promise<void>;
  decide: (id: string, body: { action: PersonMergeDecideAction; note?: string }) => Promise<void>;
  revert: (id: string) => Promise<void>;
}

export const usePersonMergeStore = create<PersonMergeState>((set, get) => ({
  merges: [], queueCount: 0, isLoading: false, isSaving: false, error: null,

  fetch: async (params = {}) => {
    set({ isLoading: true, error: null });
    try {
      const { merges } = await personMergesApi.list(params);
      set({ merges, queueCount: merges.filter((m) => m.status === 'RAISED').length, isLoading: false });
    } catch (e) {
      set({ isLoading: false, error: apiErrorMessage(e, 'Failed to load merges') });
    }
  },
  decide: async (id, body) => {
    set({ isSaving: true });
    try { await personMergesApi.decide(id, body); await get().fetch(); }
    catch (e) { throw new Error(apiErrorMessage(e, 'Failed to decide')); }
    finally { set({ isSaving: false }); }
  },
  revert: async (id) => {
    set({ isSaving: true });
    try { await personMergesApi.revert(id); await get().fetch(); }
    catch (e) { throw new Error(apiErrorMessage(e, 'Failed to undo')); }
    finally { set({ isSaving: false }); }
  },
}));
